import React, {useEffect, useState} from "react";
import {Steps, Button, Form, Input, DatePicker, Select, InputNumber, Checkbox, List, Image, Upload, message, Switch, Radio, Popconfirm} from "antd";
import { useForm, Controller } from "react-hook-form";
import {PlusOutlined, CheckOutlined, CloseOutlined} from '@ant-design/icons';
import {useAuth} from "../../contexts/AuthContext";
import dayjs from "dayjs";
import Dragger from "antd/es/upload/Dragger";
import firebaseStorage from "../../firebase";
import {ref, getMetadata, getDownloadURL} from "firebase/storage";

var __awaiter =
    (this && this.__awaiter) ||
    function (thisArg, _arguments, P, generator) {
        function adopt(value) {
            return value instanceof P
                ? value
                : new P(function (resolve) {
                    resolve(value);
                });
        }
        return new (P || (P = Promise))(function (resolve, reject) {
            function fulfilled(value) {
                try {
                    step(generator.next(value));
                } catch (e) {
                    reject(e);
                }
            }
            function rejected(value) {
                try {
                    step(generator['throw'](value));
                } catch (e) {
                    reject(e);
                }
            }
            function step(result) {
                result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
            }
            step((generator = generator.apply(thisArg, _arguments || [])).next());
        });
    };

const { Step } = Steps;
const { Option } = Select;

const TrailerForm = ({current, setCurrent, newElement, setNewElement, onSubmit, fileList, setFileList, fileListToDelete, setFileListToDelete}) => {
    const authContext = useAuth()
    const { control, setValue, getValues } = useForm();
    const [inspectionPerformedByInputValue, setInspectionPerformedByInputValue] = useState("");
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [inspectionPerformedByOptions, setInspectionPerformedByOptions] = useState([]);
    const [newComment, setNewComment] = useState({ description: "" });
    const [editingIndex, setEditingIndex] = useState(null);

    useEffect(() => {
        if (current === 0 && authContext.currentUser) {
            const fullName = `${authContext.currentUser.firstName} ${authContext.currentUser.lastName}`;

            if (!inspectionPerformedByOptions.some(option => option.value === fullName)) {
                const newOption = { value: fullName, label: fullName };
                setInspectionPerformedByOptions(prevOptions => [...prevOptions, newOption]);
            }

            setNewElement(prevState => ({
                ...prevState,
                inspection: {
                    ...prevState.inspection,
                    performedBy: fullName,
                },
            }));
        }
    }, [current]);

    const handleEditComment = (index) => {
        const existingComment = newElement.comments[index];
        setNewComment({ description: existingComment.description });
        setEditingIndex(index);
    };

    const handleUpdateComment = () => {
        if (editingIndex !== null) {
            setNewElement((prevState) => {
                const updatedComments = [...prevState.comments];
                updatedComments[editingIndex] = {
                    ...updatedComments[editingIndex],
                    description: newComment.description,
                };
                return { ...prevState, comments: updatedComments };
            });
            setNewComment({ description: "" });
            setEditingIndex(null);
        }
    };

    const handleDeleteComment = (index) => {
        setNewElement((prevState) => ({
            ...prevState,
            comments: prevState.comments.filter((_, i) => i !== index),
        }));
    };

    const handleAddComment = () => {
        if (newComment.description) {
            const newCommentData = {
                ...newComment,
                performedBy: authContext.currentUser?.firstName + " " + authContext.currentUser?.lastName,
                date: new Date().toISOString(),
            };
            setNewElement((prevState) => ({
                ...prevState,
                comments: [...(prevState.comments || []), newCommentData],
            }));
            setNewComment({ description: "" });
        }
    };

    const handleInputChange = (fieldPath, value) => {
        setNewElement((prevState) => {
            const newState = { ...prevState };
            const keys = fieldPath.split(".");
            let current = newState;

            if (keys.length > 1) {
                for (let i = 0; i < keys.length - 1; i++) {
                    const key = keys[i];
                    if (!current[key]) current[key] = {};
                    current = current[key];
                }
                current[keys[keys.length - 1]] = value;
            } else {
                current[fieldPath] = value;
            }

            return newState;
        });
    };

    const handleDateChange = (fieldPath, date) => {
        setNewElement(prevState => {
            const newState = { ...prevState };
            const keys = fieldPath.split(".");
            let current = newState;
            for (let i = 0; i < keys.length - 1; i++) {
                const key = keys[i];
                if (!current[key]) current[key] = {};
                current = current[key];
            }
            current[keys[keys.length - 1]] = date && dayjs(date).isValid() ? dayjs(date).toISOString() : null;
            return newState;
        });
    };

    const onStepChange = (value) => {
        setCurrent(value);
    };

    const getBase64 = file =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });

    const handlePreview = file =>
        __awaiter(void 0, void 0, void 0, function* () {
            if (!file.url && !file.preview) {
                file.preview = yield getBase64(file.originFileObj);
            }
            setPreviewImage(file.url || file.preview);
            setPreviewOpen(true);
        });

    const uploadButton = (
        <button style={{ border: 0, background: 'none' }} type="button">
            <PlusOutlined />
            <div style={{ marginTop: 8, padding: 4, paddingTop: 0 }}>Téléverser une image</div>
        </button>
    );

    useEffect(() => {
        fetchFiles();
    }, []);

    const fetchFiles = async () => {
        const updatedFileList = [];

        for (const key in newElement) {
            if (Array.isArray(newElement[key]) && key !== "comments") {
                const files = await Promise.all(newElement[key].map(async (url, index) => {
                    try {
                        const fileRef = ref(firebaseStorage, url);
                        const metadata = await getMetadata(fileRef);
                        const downloadUrl = await getDownloadURL(fileRef);
                        const isImage = /\.(webp|jpe?g|png)$/i.test(metadata.name);
                        return {
                            uid: `AUTO_${Date.now()}_${index}`,
                            element: key,
                            value: [
                                {
                                    lastModified: metadata.updated,    // Last modified timestamp
                                    name: metadata.name,               // File name
                                    originFileObj: metadata,             // Original file object
                                    percent: 100,                  // Upload progress percentage
                                    size: metadata.size,                // File size
                                    type: isImage ? `image/${metadata.name.split('.').pop()}` : 'application/octet-stream',
                                    uid: `rc-upload-${Date.now()}-${index}`, // Generate a unique file uid
                                    firebaseUrl: url,
                                    url: downloadUrl,
                                    status: "done"
                                }
                            ],
                        };
                    } catch (error) {
                        console.error(`Error fetching metadata for ${url}:`, error);
                        return null;
                    }
                }));
                const validFiles = files.filter(file => file !== null);
                updatedFileList.push(...validFiles);
            }
        }
        setFileList(updatedFileList);
    };

    const handleUploadCheck = async (file, element, maxCount = 2) => {
        const elementFiles = fileList.find((item) => item.element === element);
        if (elementFiles && elementFiles.value.length >= maxCount) {
            message.error(`Vous ne pouvez pas télécharger plus de ${maxCount} fichiers pour ${element}.`);
            return false;
        }
        if (file.size > 50 * 1024 * 1024) {
            message.error("Le fichier est trop volumineux. La taille maximale est de 50 Mo.");
            return false;
        }
        const existingFile = elementFiles?.value.find((item) => item.name === file.name);

        if (existingFile) {
            const newName = `${file.name.replace(/\.[^/.]+$/, "")} (${new Date().toLocaleString()})${file.name.match(/\.[^/.]+$/)[0]}`;
            file.name = newName;
            file.originFileObj.name = newName;
            setFileList((prev) => {
                return prev.map((item) =>
                    item.element === element
                        ? { ...item, value: [...item.value, file] }
                        : item
                );
            });
            message.success(`Le fichier a été renommé en ${newName} et ajouté.`);
        }
        setFileList((prev) => {
            return prev.map((item) =>
                item.element === element
                    ? { ...item, value: [...item.value, file] }
                    : item
            );
        });

        return false;
    };

    const handleFileChange = (info, element) => {
        if (info.file.status === "removed") {
            setFileListToDelete((prev) => [
                ...prev,
                { ...info.file, element }
            ]);
            setFileList((prev) => {
                return prev.map((item) =>
                    item.element === element
                        ? { ...item, value: item.value.filter(file => file.uid !== info.file.uid) }
                        : item
                );
            });
        } else {
            setFileList((prev) => {
                const existingElement = prev.find((item) => item.element === element);
                if (existingElement) {
                    return prev.map((item) =>
                        item.element === element ? { ...item, value: info.fileList } : item
                    );
                } else {
                    return [...prev, { element, value: info.fileList }];
                }
            });
        }
    };

    const formatFileName = (name, maxStart = 25, maxEnd = 10) => {
        if (!name || typeof name !== "string") return "";
        if (name.length <= maxStart + maxEnd + 3) return name;
        return `${name.slice(0, maxStart)}...${name.slice(-maxEnd)}`;
    };

    const getDraggerProps = (element, maxCount = 2, accept = ".pdf,.doc,.docx,.jpg,.png,.webp") => {
        const matchingFiles = fileList.filter((item) => item.element === element);
        let allFiles = matchingFiles.flatMap((item) => item.value);

        allFiles = allFiles.filter((file, index, self) =>
            index === self.findIndex(f => f.name === file.name && f.size === file.size && f.type === file.type)
        );

        return {
            name: "file",
            accept: accept,
            showUploadList: true,
            fileList: allFiles || [],
            multiple: true,
            maxCount: maxCount,
            itemRender: (originNode, file, fileList, actions) => {
                const isDone = file.status === "done";
                const fileUrl = file.url || file.firebaseUrl;

                const handleOpen = () => {
                    if (isDone && fileUrl) {
                        window.open(fileUrl, "_blank");
                    }
                };

                return (
                    <div className="flex items-center justify-between gap-2 px-2 py-0.5 my-0.5 border rounded bg-white">
                        <span
                            onClick={handleOpen}
                            className={`truncate max-w-[200px] cursor-pointer ${isDone ? "text-blue-600 hover:underline" : "text-gray-800"}`}
                            title={file.name}
                        >
                            {formatFileName(file.name)}
                        </span>
                        <Popconfirm
                            title="Supprimer ce fichier ?"
                            onConfirm={() => actions.remove()}
                            okText="Oui"
                            cancelText="Non"
                        >
                            <CloseOutlined className="text-red-500 cursor-pointer hover:text-red-700" />
                        </Popconfirm>
                    </div>
                );
            },
        };
    };

    const getFilteredFileList = (element) => {
        const matchingFiles = fileList.filter((item) => item.element === element);
        let allFiles = matchingFiles.flatMap((item) => item.value);

        return allFiles.filter((file, index, self) =>
            index === self.findIndex(f => f.name === file.name && f.size === file.size && f.type === file.type)
        );
    };

    const filteredFileListInsideStatus = getFilteredFileList("insideStatusPictures");
    const filteredFileListOutsideStatus = getFilteredFileList("outsideStatusPictures");

    const steps = [
        {
            title: "Informations",
            content: (
                <div className={"flex gap-3"}>
                    <div className={"flex flex-col gap-3 w-100"}>
                        <div>
                            <span className="block text-gray-500 font-medium">Libellé</span>
                            <Controller
                                name="label"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        placeholder="Libellé"
                                        value={newElement.label}
                                        onChange={(e) => handleInputChange("label", e.target.value)}
                                    />
                                )}
                            />
                        </div>
                        <div>
                            <span className="block text-gray-500 font-medium">Marque</span>
                            <Controller
                                name="brand"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        placeholder="Marque"
                                        value={newElement.brand}
                                        onChange={(e) => handleInputChange("brand", e.target.value)}
                                    />
                                )}
                            />
                        </div>
                        <div>
                            <span className="block text-gray-500 font-medium">Plaque d'immatriculation <span className={"text-custom-blue"}>*</span></span>
                            <Controller
                                name="licensePlate"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        placeholder="Plaque d'immatriculation"
                                        value={newElement.licensePlate}
                                        onChange={(e) => handleInputChange("licensePlate", e.target.value)}
                                    />
                                )}
                            />
                        </div>
                        <div>
                            <span className="block text-gray-500 font-medium">Propriétaire <span className={"text-custom-blue"}>*</span></span>
                            <Controller
                                name="owner"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        placeholder="Propriétaire"
                                        value={newElement.owner}
                                        onChange={(e) => handleInputChange("owner", e.target.value)}
                                    />
                                )}
                            />
                        </div>
                        <div>
                            <span className="block text-gray-500 font-medium">Locataire</span>
                            <Controller
                                name="rentalCompany"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        placeholder="Locataire"
                                        value={newElement.rentalCompany}
                                        onChange={(e) => handleInputChange("rentalCompany", e.target.value)}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div className={"flex flex-col gap-3 w-100"}>
                        <div>
                            <span className="block text-gray-500 font-medium">
                                Contrat de location
                            </span>
                            <Controller
                                name="rentalSheet"
                                control={control}
                                render={({ field }) => (
                                    <Dragger
                                        {...getDraggerProps("rentalSheet")}
                                        onChange={(info) => handleFileChange(info, "rentalSheet")}
                                        beforeUpload={(file) => handleUploadCheck(file, "rentalSheet")}
                                        className="w-100 h-16 mb-0.5 mt-0.5 flex items-center justify-center text-center gap-2.5 overflow-clip"
                                    >
                                        <span className={"w-100"}>Cliquez ou Déposez les documents</span>
                                    </Dragger>
                                )}
                            />
                        </div>
                        <div className={"flex gap-2"}>
                            <p>Constat</p>
                            <Controller
                                name="anglesMortsOK"
                                control={control}
                                render={({ field }) => (
                                    <Switch
                                        {...field}
                                        className={"ml-2"}
                                        checked={newElement.report}
                                        onChange={(checked) => handleInputChange("report", checked)}
                                        checkedChildren="Oui"
                                        unCheckedChildren="Non"
                                    />
                                )}
                            />
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "Contrôles",
            content: (
                <div className={"flex flex-col gap-3"}>
                    <div className={"flex gap-3"}>
                        <div className={"flex flex-col gap-3 w-100"}>
                            <div className="flex gap-3 mr-11 pr-0.5">
                                <div className="flex flex-col w-50">
                                    <span className="block text-gray-500 font-medium mb-0.5">
                                        Date de la dernière inspection <span className={"text-custom-blue"}>*</span>
                                    </span>
                                    <Controller
                                        name="inspection.lastPerformed"
                                        control={control}
                                        render={({ field }) => (
                                            <DatePicker
                                                {...field}
                                                format={{
                                                    format: 'DD/MM/YYYY',
                                                    type: 'mask',
                                                }}
                                                value={newElement.inspection?.lastPerformed ? dayjs(newElement.inspection?.lastPerformed) : handleDateChange("inspection.lastPerformed", dayjs())}
                                                className={"w-100"}
                                                placeholder="Dernière inspection"
                                                onChange={(date) => handleDateChange("inspection.lastPerformed", date)}
                                            />
                                        )}
                                    />
                                </div>
                                <div className={"flex flex-col w-50"}>
                                    <span className="block text-gray-500 font-medium mb-0.5">
                                        Réalisée par <span className={"text-custom-blue"}>*</span>
                                    </span>
                                    <Controller
                                        name="inspection.performedBy.person"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                            <Select
                                                showSearch
                                                placeholder="Réalisée par"
                                                optionFilterProp="label"
                                                options={inspectionPerformedByOptions}
                                                value={newElement.inspection?.performedBy ? newElement.inspection?.performedBy : authContext.currentUser?.firstName + " " + authContext.currentUser?.lastName}
                                                onChange={(value) => {
                                                    if (!inspectionPerformedByOptions.some(option => option.value === value)) {
                                                        const newOption = { value, label: value };
                                                        setInspectionPerformedByOptions([...inspectionPerformedByOptions, newOption]);
                                                    }
                                                    field.onChange(value);
                                                    setNewElement(prevState => ({
                                                        ...prevState,
                                                        inspection: {
                                                            ...prevState.inspection,
                                                            performedBy: value,
                                                        },
                                                    }));
                                                }}
                                                onSearch={setInspectionPerformedByInputValue}
                                                onBlur={() => {
                                                    if (inspectionPerformedByInputValue && !inspectionPerformedByOptions.some(opt => opt.value === inspectionPerformedByInputValue)) {
                                                        const newOption = { value: inspectionPerformedByInputValue, label: inspectionPerformedByInputValue };
                                                        setInspectionPerformedByOptions([...inspectionPerformedByOptions, newOption]);
                                                        field.onChange(inspectionPerformedByInputValue);
                                                        setNewElement(prevState => ({
                                                            ...prevState,
                                                            inspection: {
                                                                ...prevState.inspection,
                                                                performedBy: inspectionPerformedByInputValue,
                                                            },
                                                        }));
                                                    }
                                                }}
                                                onInputKeyDown={(e) => {
                                                    if (e.key === "Enter" && inspectionPerformedByInputValue) {
                                                        if (!inspectionPerformedByOptions.some(opt => opt.value === inspectionPerformedByInputValue)) {
                                                            const newOption = { value: inspectionPerformedByInputValue, label: inspectionPerformedByInputValue };
                                                            setInspectionPerformedByOptions([...inspectionPerformedByOptions, newOption]);
                                                        }
                                                        field.onChange(inspectionPerformedByInputValue);
                                                        setNewElement(prevState => ({
                                                            ...prevState,
                                                            inspection: {
                                                                ...prevState.inspection,
                                                                performedBy: inspectionPerformedByInputValue,
                                                            },
                                                        }));
                                                    }
                                                }}
                                                allowClear
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className={"flex gap-3"}>
                                <div className={"flex flex-col w-50"}>
                                    <span className="block text-gray-500 font-medium mb-0.5">Date du dernier contrôle technique <span className={"text-custom-blue"}>*</span></span>
                                    <Controller
                                        name="technicalReview.lastPerformed"
                                        control={control}
                                        render={({ field }) => (
                                            <DatePicker
                                                {...field}
                                                format={{
                                                    format: 'DD/MM/YYYY',
                                                    type: 'mask',
                                                }}
                                                value={newElement.technicalReview?.lastPerformed ? dayjs(newElement.technicalReview?.lastPerformed) : undefined}
                                                className={"w-100"}
                                                placeholder="Dernier contrôle technique"
                                                onChange={(date) => handleDateChange("technicalReview.lastPerformed", date)}
                                            />
                                        )}
                                    />
                                </div>
                                <div className={"flex flex-col w-50"}>
                                    <span className="block text-gray-500 font-medium mb-0.5">Date du prochain contrôle technique</span>
                                    <Controller
                                        name="technicalReview.nextScheduled"
                                        control={control}
                                        render={({ field }) => (
                                            <DatePicker
                                                format={{
                                                    format: 'DD/MM/YYYY',
                                                    type: 'mask',
                                                }}
                                                {...field}
                                                value={newElement.technicalReview?.nextScheduled ? dayjs(newElement.technicalReview?.nextScheduled) : undefined}
                                                className={"w-100"}
                                                placeholder="Prochain contrôle technique"
                                                onChange={(date) => handleDateChange("technicalReview.nextScheduled", date)}
                                            />
                                        )}
                                    />
                                </div>
                                <Controller name="inspection.lastPerformed" control={control} render={({ field }) => <Button disabled={!newElement.technicalReview?.nextScheduled} onClick={() => {handleDateChange("technicalReview.lastPerformed", newElement.technicalReview?.nextScheduled); handleDateChange("technicalReview.nextScheduled", null)}} icon={<CheckOutlined />} className={"mt-auto text-green-600"}/>} />
                            </div>
                        </div>
                    </div>
                    <div className={"flex gap-3"}>
                        <div className={"w-50"}>
                            <span className="block text-gray-500 font-medium mb-0.5">Date d'expiration de l'extincteur <span className={"text-custom-blue"}>*</span></span>
                            <Controller
                                name="extinguisher.expiryDate"
                                control={control}
                                render={({ field }) => (
                                    <DatePicker
                                        {...field}
                                        format={{
                                            format: 'DD/MM/YYYY',
                                            type: 'mask',
                                        }}
                                        value={newElement.extinguisher?.expiryDate ? dayjs(newElement.extinguisher?.expiryDate) : undefined}
                                        className={"w-100"}
                                        placeholder="Expiration extincteur"
                                        onChange={(date) => handleDateChange("extinguisher.expiryDate", date)}
                                    />
                                )}
                            />
                        </div>
                        <Controller name="extinguisher.needsReplacement" control={control} render={({ field }) => <Checkbox
                            {...field}
                            className={"mt-auto mb-1.5 w-50 mr-11 pr-0.5"}
                            checked={newElement.extinguisher.needsReplacement}
                            onChange={(e) => handleInputChange("extinguisher.needsReplacement", e.target.checked)}
                        >Extincteur à remplacer</Checkbox>} />
                    </div>
                    <Controller
                        name="hasTailgates"
                        control={control}
                        render={({ field }) => (
                            <Checkbox
                                {...field}
                                className={"mt-3"}
                                checked={newElement.hasTailgates}
                                onChange={(e) => {handleInputChange("hasTailgates", e.target.checked); !e.target.checked && handleInputChange("transpal", false); !e.target.checked && handleInputChange("transpalType", false); !e.target.checked && handleInputChange("tailgates.lastPerformed", null); !e.target.checked && handleInputChange("tailgates.nextScheduled", null)}}
                            >
                                Est-ce un porteur avec hayon ?
                            </Checkbox>
                        )}
                    />
                    <div className={"flex gap-3"}>
                        <div className={"flex flex-col w-50"}>
                            <span className="block text-gray-500 font-medium mb-0.5">Date du dernier contrôle hayon <span className={"text-custom-blue"}>*</span></span>
                            <Controller
                                name="tailgates.lastPerformed"
                                disabled={!newElement.hasTailgates}
                                control={control}
                                render={({ field }) => (
                                    <DatePicker
                                        {...field}
                                        format={{
                                            format: 'DD/MM/YYYY',
                                            type: 'mask',
                                        }}
                                        value={newElement.tailgates?.lastPerformed ? dayjs(newElement.tailgates?.lastPerformed) : undefined}
                                        className={"w-100"}
                                        placeholder="Dernier contrôle hayon"
                                        onChange={(date) => handleDateChange("tailgates.lastPerformed", date)}
                                    />
                                )}
                            />
                        </div>
                        <div className={"flex flex-col w-50"}>
                            <span className="block text-gray-500 font-medium mb-0.5">Date du prochain contrôle hayon</span>
                            <Controller
                                name="tailgates.nextScheduled"
                                disabled={!newElement.hasTailgates}
                                control={control}
                                render={({ field }) => (
                                    <DatePicker
                                        {...field}
                                        format={{
                                            format: 'DD/MM/YYYY',
                                            type: 'mask',
                                        }}
                                        value={newElement.tailgates?.nextScheduled ? dayjs(newElement.tailgates?.nextScheduled) : undefined}
                                        className={"w-100"}
                                        placeholder="Prochain contrôle hayon"
                                        onChange={(date) => handleDateChange("tailgates.nextScheduled", date)}
                                    />
                                )}
                            />
                        </div>
                        <Controller name="tailgates.realized" control={control} render={({ field }) => <Button disabled={!newElement.tailgates?.nextScheduled} onClick={() => {handleDateChange("tailgates.lastPerformed", newElement.tailgates?.nextScheduled); handleDateChange("tailgates.nextScheduled", null)}} icon={<CheckOutlined />} className={"mt-auto text-green-600"}/>} />
                    </div>
                    <div className={"flex gap-3 mt-1.5"}>
                        <div className={"flex gap-2 my-auto"}>
                            <p className={"my-auto"}>Équipé d'un transpalette ?</p>
                            <Controller
                                name="transpal"
                                control={control}
                                render={({ field }) => (
                                    <Switch
                                        {...field}
                                        disabled={!newElement.hasTailgates}
                                        checked={newElement.transpal}
                                        className={"my-auto"}
                                        onChange={(checked) => {handleInputChange("transpal", checked); !checked && handleInputChange("transpalType", false)}}
                                        checkedChildren="Oui"
                                        unCheckedChildren="Non"
                                    />
                                )}
                            />
                        </div>
                        <div className={"flex gap-2 my-auto"}>
                            <p className={"my-auto"}>Électrique / Manuel</p>
                            <Controller
                                name="transpalType"
                                size="small"
                                control={control}
                                render={({ field }) => (
                                    <Radio.Group
                                        {...field}
                                        buttonStyle="solid"
                                        disabled={!newElement.transpal}
                                        value={newElement.transpalType ? "true" : "false"}
                                        onChange={(e) => handleInputChange("transpalType", e.target.value === "true")}
                                    >
                                        <Radio.Button value={"false"}>Manuel</Radio.Button>
                                        <Radio.Button value={"true"}>Électrique</Radio.Button>
                                    </Radio.Group>
                                )}
                            />
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "Status",
            content: (
                <div className={"flex flex-col gap-3"}>
                    <div className={"flex gap-3"}>
                        <div className={"flex flex-col w-30 justify-center"}>
                            <span className="block text-gray-500 font-medium mb-0.5">État intérieur (0-10) <span className={"text-custom-blue"}>*</span></span>
                            <Controller
                                name="insideStatus"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <InputNumber
                                        {...field}
                                        className={"w-100"}
                                        min={0} max={10}
                                        value={newElement.insideStatus}
                                        onChange={(value) => handleInputChange("insideStatus", value)}
                                    />
                                )}
                            />
                        </div>
                        <div className={"border border-dashed w-70 p-1 rounded-xl"}>
                            <Upload
                                listType="picture-card"
                                fileList={filteredFileListInsideStatus}
                                onPreview={handlePreview}
                                multiple={true}
                                accept={".jpg,.png,.webp,.HEIC"}
                                onChange={(info) => handleFileChange(info, "insideStatusPictures")}
                                beforeUpload={(file) => handleUploadCheck(file, "insideStatusPictures", 18)}
                            >
                                {filteredFileListInsideStatus.length >= 18 ? null : uploadButton}
                            </Upload>

                            {previewImage && (
                                <Image
                                    wrapperStyle={{ display: 'none' }}
                                    preview={{
                                        visible: previewOpen,
                                        onVisibleChange: (visible) => setPreviewOpen(visible),
                                        afterOpenChange: (visible) => !visible && setPreviewImage(''),
                                    }}
                                    src={previewImage}
                                />
                            )}
                        </div>
                    </div>
                    <div className={"flex gap-3"}>
                        <div className={"flex flex-col w-30 justify-center"}>
                            <span className="block text-gray-500 font-medium mb-0.5">État extérieur (0-10) <span className={"text-custom-blue"}>*</span></span>
                            <Controller
                                name="outsideStatus"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <InputNumber
                                        {...field}
                                        className={"w-100"}
                                        min={0} max={10}
                                        value={newElement.outsideStatus}
                                        onChange={(value) => handleInputChange("outsideStatus", value)}
                                    />
                                )}
                            />
                        </div>
                        <div className={"border border-dashed w-70 p-1 rounded-xl"}>
                            <Upload
                                listType="picture-card"
                                fileList={filteredFileListOutsideStatus}
                                onPreview={handlePreview}
                                multiple={true}
                                accept={".jpg,.png,.webp,.HEIC"}
                                onChange={(info) => handleFileChange(info, "outsideStatusPictures")}
                                beforeUpload={(file) => handleUploadCheck(file, "outsideStatusPictures", 18)}
                            >
                                {filteredFileListOutsideStatus.length >= 18 ? null : uploadButton}
                            </Upload>

                            {previewImage && (
                                <Image
                                    wrapperStyle={{ display: 'none' }}
                                    preview={{
                                        visible: previewOpen,
                                        onVisibleChange: (visible) => setPreviewOpen(visible),
                                        afterOpenChange: (visible) => !visible && setPreviewImage(''),
                                    }}
                                    src={previewImage}
                                />
                            )}
                        </div>
                    </div>
                    <div className={"flex gap-3"}>
                        <div className={"w-30"}>
                        <span className="block text-gray-500 font-medium mb-0.5">Disponibilité <span className={"text-custom-blue"}>*</span></span>
                            <Controller
                                name="availability.status"
                                control={control}
                                render={({ field }) => (
                                    <Select className="w-100" {...field} placeholder="Statut de disponibilité" value={newElement.availability.status} onChange={(e) => e === "Disponible" ? (handleInputChange("availability.description", ""), handleInputChange("availability.status", e)) : handleInputChange("availability.status", e)}>
                                        <Option value="Disponible">Disponible</Option>
                                        <Option value="Maintenance programée">Maintenance programée</Option>
                                        <Option value="Contre-visite">Contre-visite</Option>
                                        <Option value="En maintenance">En maintenance</Option>
                                        <Option value="Hors service">Hors service</Option>
                                    </Select>
                                )}
                            />
                        </div>
                        <div className={"w-70"}>
                            <span className="block text-gray-500 font-medium mb-0.5">Description</span>
                            <Controller
                                disabled={newElement.availability.status === "Disponible"}
                                name="availability.description"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        placeholder="Description"
                                        value={newElement.availability.description}
                                        onChange={(e) => handleInputChange("availability.description", e.target.value)}
                                    />
                                )}
                            />
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "Commentaires",
            content: (
                <>
                    <span className="block text-gray-500 font-medium mb-0.5">
                        Commentaires
                    </span>
                    <Controller
                        name="comments"
                        control={control}
                        render={({ field }) => (
                            <>
                                <List
                                    className={"max-h-[435px] overflow-scroll"}
                                    dataSource={newElement.comments || []}
                                    renderItem={(comment, index) => (
                                        <List.Item key={index}>
                                            <div className="border p-3 rounded-md w-full">
                                                <p className="text-sm text-gray-700">
                                                    <strong>{comment.performedBy}</strong> - {new Date(comment.date).toLocaleDateString()}
                                                </p>
                                                <p className="text-sm text-gray-600 overflow-hidden">{comment.description}</p>
                                                <div className="flex gap-4 justify-end">
                                                    <Button type="link" className={"p-0"} onClick={() => handleEditComment(index)}>Modifier</Button>
                                                    <Button type="link" className={"p-0"} danger onClick={() => handleDeleteComment(index)}>Supprimer</Button>
                                                </div>
                                            </div>
                                        </List.Item>
                                    )}
                                />

                                <div className="mt-2">
                                    <p className="font-medium text-gray-600 mb-0.5">
                                        {editingIndex !== null ? "Modifier le commentaire :" : "Ajouter un commentaire :"}
                                    </p>
                                    <Input.TextArea
                                        rows={3}
                                        placeholder="Votre commentaire..."
                                        value={newComment.description}
                                        onChange={(e) => setNewComment({ ...newComment, description: e.target.value })}
                                    />
                                    <Button
                                        type="dashed"
                                        className="mt-2 w-full"
                                        onClick={editingIndex !== null ? handleUpdateComment : handleAddComment}
                                    >
                                        {editingIndex !== null ? "Mettre à jour" : "Ajouter"}
                                    </Button>
                                </div>
                            </>
                        )}
                    />
                </>
            )
        }
    ];

    return (
        <Form layout="vertical" onFinish={() => (onSubmit)}>
            <Steps className={"max-w-4xl mx-auto"} current={current} onChange={onStepChange}>
                {steps.map((step, index) => (<Step key={index} title={step.title} />))}
            </Steps>
            <div className={"w-100"}>
                <div style={{ marginTop: 30 }}>{steps[current].content}</div>
            </div>
        </Form>
    );
};

export default TrailerForm;
